import { type ReactElement } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Favicon } from '@/components/ui/Favicon';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { openUntrustedUrl } from '@/utils/external-links';

import type { WikiConfidence, WikiFact } from '../people-wiki.types';

const CONFIDENCE_MARKERS: Partial<
  Record<WikiConfidence, { label: string; className: string }>
> = {
  medium: { label: 'unsure', className: 'text-foreground-subtle' },
  low: { label: 'low confidence', className: 'text-warning-11' },
};

const SOURCE_MARK_CLASS_NAME =
  'ml-1.5 inline-flex rounded-sm align-middle opacity-60 transition-opacity hover:opacity-100 focus-ring';

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const SourceMark = ({
  label,
  description,
  onClick,
  children,
}: {
  label: string;
  description?: string;
  onClick?: () => void;
  children: ReactElement;
}) => (
  <Tooltip
    label={label}
    description={description}
    side="top"
    delayDuration={200}
  >
    {onClick ? (
      <button
        type="button"
        aria-label={label}
        className={SOURCE_MARK_CLASS_NAME}
        onClick={onClick}
      >
        {children}
      </button>
    ) : (
      <span className={SOURCE_MARK_CLASS_NAME}>{children}</span>
    )}
  </Tooltip>
);

export const FactRow = ({
  fact,
  onRemove,
}: {
  fact: WikiFact;
  onRemove?: () => void;
}) => {
  const confidenceMarker = CONFIDENCE_MARKERS[fact.confidence];
  return (
    <p className="group/fact text-sm leading-6 text-foreground">
      {fact.text}
      {fact.source === 'agent' && (
        <SourceMark label="From Littlebird">
          <AppLogo size={12} customColor="currentColor" decorative />
        </SourceMark>
      )}
      {fact.citations.map((citation, index) => (
        <SourceMark
          key={`${index}-${citation.url}`}
          label={citation.title || hostOf(citation.url)}
          description={citation.excerpt || undefined}
          onClick={() => openUntrustedUrl(citation.url)}
        >
          <Favicon
            domain={hostOf(citation.url)}
            className="size-3.5 rounded-sm"
          />
        </SourceMark>
      ))}
      {confidenceMarker && (
        <span className={cn('ml-1.5 text-xs', confidenceMarker.className)}>
          {confidenceMarker.label}
        </span>
      )}
      {onRemove && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            variant="ghost"
            aria-label="Actions for this fact"
            className="ml-1 inline-flex size-5 justify-center p-0 align-middle opacity-0 transition-opacity focus-visible:opacity-100 group-hover/fact:opacity-60 data-[state=open]:opacity-100"
          >
            <Icons.moreHorizontal className="size-3.5" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start">
            <DropdownMenu.ItemWithConfirm onConfirm={onRemove}>
              Remove fact
            </DropdownMenu.ItemWithConfirm>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
    </p>
  );
};
